/**
 * Motion recordings for the rep detection (todos 7 and 8): what the hidden "Bewegung aufzeichnen" screen records during
 * one timed set. The app keeps the recordings in its database and exports them all at once as one file (a bundle); the
 * owner copies it to `tests/fixtures/motion/`, where the tests of todo 8 run the detector over every recording and
 * compare with the reps the owner tapped.
 *
 * Pure: the sensors are read by `src/ui/motionSource.ts` (the native plugin on Android, DeviceMotionEvent in the
 * browser), which hands over a `RawRecording`; `buildRecording` turns it into the file.
 */
import type { Measure } from "./exercises";

/** Where the phone was during the set (question Q2 in CLAUDE.md). */
export const PLACEMENTS = ["pocket", "upperArm", "hand", "floor", "other"] as const;
export type Placement = (typeof PLACEMENTS)[number];

/** Samples of one sensor as columns: `t` in ms since the recording started, x/y/z in the phone's axes. */
export interface Columns {
  t: number[];
  x: number[];
  y: number[];
  z: number[];
}

/** Moments the recorder notes on its own clock (the same clock as the samples). */
export type MarkKind = "tap" | "untap" | "setStart" | "setEnd";
export interface Mark {
  kind: MarkKind;
  t: number;
}

/** What a motion source returns when it stops. */
export interface RawRecording {
  /** accelerometer including gravity, m/s² */
  accel: Columns;
  /** gyroscope, rad/s; null when the phone has none */
  gyro: Columns | null;
  marks: Mark[];
  /** "android" (SensorManager, as fast as the phone allows); "web" (DeviceMotionEvent, about 60 Hz in Chrome) */
  source: "android" | "web";
  /** sensor names and the like, as far as the source knows them */
  device: Record<string, string>;
}

export const MOTION_FILE_VERSION = 1;

/** The saved file (one set). */
export interface MotionRecording {
  app: "workout-helper";
  kind: "motion";
  version: number;
  /** ms since 1970, when the recording started */
  recordedAt: number;
  appVersion: string;
  source: "android" | "web";
  device: Record<string, string>;
  exercise: { id: string; name: string; equipment: string; measure: Measure };
  placement: Placement;
  /** planned set duration in s */
  durationSec: number;
  /** the set on the recording's clock (ms); before and after it the phone was being stowed or taken out */
  setStart: number;
  setEnd: number;
  /** the ground truth: reps done, as the owner confirmed them after the set */
  reps: number;
  /** what the rep detection counted live (todo 8: sensor sets in a workout and the Zähltest); absent before 0.3.6 */
  counted?: number;
  /** "workout": a sensor set of a workout (since 0.3.7; only the last ones per exercise are kept); else absent */
  origin?: "workout";
  /** when the owner tapped (screen or volume key), ms; since 0.3.3 nobody taps during the set, so mostly empty */
  taps: number[];
  notes: string;
  /** measured sample rates in Hz */
  rateHz: { accel: number; gyro: number | null };
  accel: Columns;
  gyro: Columns | null;
}

export interface RecordingInfo {
  recordedAt: number;
  appVersion: string;
  exercise: MotionRecording["exercise"];
  placement: Placement;
  durationSec: number;
  reps: number;
  counted?: number;
  origin?: "workout";
  notes: string;
}

const round = (n: number, digits: number) => {
  const f = 10 ** digits;
  return Math.round(n * f) / f;
};

/** Rounds to what the sensors resolve (keeps the files small): t to 0.01 ms, values to 4 decimals. */
export function roundColumns(c: Columns): Columns {
  return {
    t: c.t.map((v) => round(v, 2)),
    x: c.x.map((v) => round(v, 4)),
    y: c.y.map((v) => round(v, 4)),
    z: c.z.map((v) => round(v, 4)),
  };
}

/** Median sample rate in Hz (robust against the gaps a busy phone leaves); 0 with fewer than 2 samples. */
export function sampleRate(t: number[]): number {
  if (t.length < 2) return 0;
  const gaps = t.slice(1).map((v, i) => v - t[i]).filter((g) => g > 0).sort((a, b) => a - b);
  if (!gaps.length) return 0;
  return round(1000 / gaps[Math.floor(gaps.length / 2)], 1);
}

/**
 * The samples at a fixed rate, linearly interpolated, from the first sample to the last: the native recordings (a few
 * hundred Hz) as the web source would give them (about 60 Hz), so the rep detection can be tested at both rates.
 */
export function resample(c: Columns, hz: number): Columns {
  const out: Columns = { t: [], x: [], y: [], z: [] };
  const n = c.t.length;
  if (!n || hz <= 0) return out;
  const step = 1000 / hz;
  let i = 0;
  const count = Math.floor((c.t[n - 1] - c.t[0]) / step + 1e-9) + 1;
  for (let k = 0; k < count; k++) {
    const t = c.t[0] + k * step;
    while (i < n - 2 && c.t[i + 1] < t) i++;
    const j = Math.min(i + 1, n - 1);
    const span = c.t[j] - c.t[i];
    const f = span > 0 ? Math.min(1, Math.max(0, (t - c.t[i]) / span)) : 0;
    out.t.push(t);
    out.x.push(c.x[i] + (c.x[j] - c.x[i]) * f);
    out.y.push(c.y[i] + (c.y[j] - c.y[i]) * f);
    out.z.push(c.z[i] + (c.z[j] - c.z[i]) * f);
  }
  return out;
}

/** A recording with its sensors resampled to `hz` (see `resample`); marks, taps and the set window stay. */
export function resampleRecording(r: MotionRecording, hz: number): MotionRecording {
  const accel = resample(r.accel, hz);
  const gyro = r.gyro && resample(r.gyro, hz);
  return { ...r, accel, gyro, rateHz: { accel: sampleRate(accel.t), gyro: gyro ? sampleRate(gyro.t) : null } };
}

/** The tap times after applying the undos ("untap" removes the latest tap before it). */
export function tapTimes(marks: Mark[]): number[] {
  const taps: number[] = [];
  for (const m of [...marks].sort((a, b) => a.t - b.t)) {
    if (m.kind === "tap") taps.push(m.t);
    else if (m.kind === "untap") taps.pop();
  }
  return taps;
}

/** The file for a stopped recording. Without a setStart/setEnd mark the whole recording counts as the set. */
export function buildRecording(raw: RawRecording, info: RecordingInfo): MotionRecording {
  const accel = roundColumns(raw.accel);
  const gyro = raw.gyro && raw.gyro.t.length ? roundColumns(raw.gyro) : null;
  const last = (kind: MarkKind) => raw.marks.filter((m) => m.kind === kind).at(-1)?.t;
  const end = Math.max(accel.t.at(-1) ?? 0, gyro?.t.at(-1) ?? 0);
  return {
    app: "workout-helper",
    kind: "motion",
    version: MOTION_FILE_VERSION,
    recordedAt: info.recordedAt,
    appVersion: info.appVersion,
    source: raw.source,
    device: raw.device,
    exercise: info.exercise,
    placement: info.placement,
    durationSec: info.durationSec,
    setStart: round(last("setStart") ?? 0, 2),
    setEnd: round(last("setEnd") ?? end, 2),
    reps: info.reps,
    ...(info.counted !== undefined ? { counted: info.counted } : {}),
    ...(info.origin ? { origin: info.origin } : {}),
    taps: tapTimes(raw.marks).map((v) => round(v, 2)),
    notes: info.notes.trim(),
    rateHz: { accel: sampleRate(accel.t), gyro: gyro ? sampleRate(gyro.t) : null },
    accel,
    gyro,
  };
}

/** A stored recording: the file content plus the record id (collection `RECORDINGS` in storage.ts, not in backups). */
export type StoredRecording = MotionRecording & { id: string };

/** The export of all recordings on the phone: one file, written in pieces (see `bundleChunks`). */
export interface MotionBundle {
  app: "workout-helper";
  kind: "motionBundle";
  version: number;
  /** ms since 1970 */
  exportedAt: number;
  recordings: MotionRecording[];
}

/**
 * The bundle file as text chunks, one per recording: a recording is a few MB at a fast sample rate, and the Android
 * save plugin takes the file piece by piece instead of as one huge string. Joined, the chunks are a `MotionBundle`.
 */
export function bundleChunks(recordings: MotionRecording[], exportedAt: number): string[] {
  const head = JSON.stringify({ app: "workout-helper", kind: "motionBundle", version: MOTION_FILE_VERSION, exportedAt });
  return [
    head.slice(0, -1) + ',"recordings":[',
    ...recordings.map((r, i) => (i ? "," : "") + JSON.stringify(r)),
    "]}",
  ];
}

/** File name of a bundle: date and time, number of recordings, e.g. `motion-2026-10-16-1432-5.json`. */
export function bundleName(exportedAt: number, count: number): string {
  const d = new Date(exportedAt);
  const p = (n: number) => String(n).padStart(2, "0");
  return `motion-${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}-${count}.json`;
}

export type MotionReadError = "notJson" | "notMotion" | "newer" | "damaged";

const isNumArray = (v: unknown): v is number[] => Array.isArray(v) && v.every((n) => typeof n === "number" && Number.isFinite(n));

function validColumns(c: unknown): c is Columns {
  if (!c || typeof c !== "object") return false;
  const { t, x, y, z } = c as Columns;
  if (![t, x, y, z].every(isNumArray)) return false;
  if (x.length !== t.length || y.length !== t.length || z.length !== t.length) return false;
  return t.every((v, i) => i === 0 || v >= t[i - 1]);
}

function checkRecording(data: unknown): { ok: true; recording: MotionRecording } | { ok: false; error: MotionReadError } {
  const r = data as MotionRecording;
  if (!r || typeof r !== "object" || r.app !== "workout-helper" || r.kind !== "motion") return { ok: false, error: "notMotion" };
  if (typeof r.version !== "number" || r.version > MOTION_FILE_VERSION) return { ok: false, error: "newer" };
  const ok = validColumns(r.accel) && r.accel.t.length > 0 && (r.gyro === null || validColumns(r.gyro))
    && Number.isInteger(r.reps) && r.reps >= 0 && isNumArray(r.taps)
    && typeof r.setStart === "number" && typeof r.setEnd === "number" && r.setEnd >= r.setStart
    && (PLACEMENTS as readonly string[]).includes(r.placement)
    && !!r.exercise && typeof r.exercise.name === "string" && typeof r.exercise.id === "string";
  return ok ? { ok: true, recording: r } : { ok: false, error: "damaged" };
}

/**
 * Reads and checks a recording file (the fixtures in the tests of todo 8): a bundle, or a single recording as 0.3.0
 * saved it. One damaged recording refuses the whole file.
 */
export function readRecordings(text: string): { ok: true; recordings: MotionRecording[] } | { ok: false; error: MotionReadError } {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return { ok: false, error: "notJson" };
  }
  const b = data as MotionBundle;
  if (!b || typeof b !== "object" || b.kind !== "motionBundle") {
    const r = checkRecording(data);
    return r.ok ? { ok: true, recordings: [r.recording] } : r;
  }
  if (b.app !== "workout-helper") return { ok: false, error: "notMotion" };
  if (typeof b.version !== "number" || b.version > MOTION_FILE_VERSION) return { ok: false, error: "newer" };
  if (!Array.isArray(b.recordings)) return { ok: false, error: "damaged" };
  const recordings: MotionRecording[] = [];
  for (const item of b.recordings) {
    const r = checkRecording(item);
    if (!r.ok) return r.error === "newer" ? r : { ok: false, error: "damaged" };
    recordings.push(r.recording);
  }
  return { ok: true, recordings };
}
